// A payout contract's PDF (aldoo-na store/payouts/contracts, payouts_contract_file):
// in the framework's PDF viewer — the words in the portal's language — or
// downloaded. Shared by the contract card, its history and the signing.
import React, { useEffect, useMemo, useRef, useState } from "react"
import PDFViewer from "aldoo-ra/PDFViewer"
import { showErrorToast } from "aldoo-ra/Toast"
import { PayoutsContractFile, PayoutsContractSample } from "../AdminShared/api"
import usePortalTexts from "../Vendor/texts/usePortalTexts"
import { PAYOUTS } from "../Vendor/texts/bundles"
import { pdfBytes, downloadPdf } from "./pdf"

export async function download(contractId, which, t) {
  const res = await PayoutsContractFile(contractId, which).catch(() => null)
  if (!downloadPdf(res)) showErrorToast(t`Could not load the contract`)
}

/** The viewer's and the sign tools' words, in the portal's language. */
export const viewerTexts = (t) => ({
  loading: t`Loading…`,
  error: t`Could not load the contract`,
  zoomOut: t`Zoom out`,
  zoomIn: t`Zoom in`,
  fitWidth: t`Fit width`,
  prevPage: t`Previous page`,
  nextPage: t`Next page`,
  download: t`Download`,
  pages: t`Pages`,
  showPages: t`Show the pages`,
  hidePages: t`Hide the pages`,
  pageN: t`Page %s`
})

/**
 * The contract's PDF in the viewer: loaded once per contract and version of it.
 * `sidePanel`: beside the pages (the sign tools), there while the file loads too;
 * `downloadable`: a download in the viewer's toolbar; `onFile`: told the file once loaded (a host's own download). `sampleFor` (an organization id) in place of
 * a contract: the contract it would be sent, with sample data (payouts_contract_sample).
 */
export default function ContractPdf({ contractId, which, sampleFor = null, sidePanel, downloadable = false, onFile, height = "80vh" }) {
  const t = usePortalTexts(PAYOUTS)
  const [answer, setAnswer] = useState(null)
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    let gone = false
    setAnswer(null)
    setFailed(false)
    ;(sampleFor ? PayoutsContractSample(sampleFor) : PayoutsContractFile(contractId, which))
      .then((res) => {
        if (gone) return
        if (res?.pdf) setAnswer(res)
        else setFailed(true)
      })
      .catch(() => !gone && setFailed(true))
    return () => {
      gone = true
    }
  }, [contractId, which, sampleFor])
  const onFileRef = useRef(onFile)
  onFileRef.current = onFile
  useEffect(() => {
    onFileRef.current?.(answer)
  }, [answer])
  const bytes = useMemo(() => pdfBytes(answer), [answer])
  const texts = useMemo(() => viewerTexts(t), [t])

  if (failed)
    return (
      <div className="w-full rounded-md border border-admin_bordered bg-admin_neutral_1 p-6 text-sm text-admin_text_secondary" data-testid="contract-preview">
        {t`Could not load the contract`}
      </div>
    )
  return (
    <div className="w-full" data-testid="contract-preview">
      <PDFViewer src={bytes} height={height} sidePanel={sidePanel} onDownload={downloadable && answer ? () => downloadPdf(answer) : undefined} texts={texts} />
    </div>
  )
}

